import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import type * as React from "react";
import { cn } from "../cn";
import { FOCUS_RING } from "./focus-ring";

// Design spec §3 row 0 and §12.3: the cobalt "C" tile, the one place the brand is drawn (a
// real logo replaces the glyph here and nowhere else). A tile, or a link with `render`.
const SIZES = {
  24: "size-6 rounded-sm text-xs",
  32: "size-8 rounded-md text-sm",
  40: "size-10 rounded-[10px] text-base",
} as const;

type BrandMarkProps = useRender.ComponentProps<"span"> & {
  /** Accessible name (the product or company name). */
  name: string;
  /** The drawn glyph; defaults to the name's first letter. */
  glyph?: string;
  size?: keyof typeof SIZES;
};

function BrandMark({ name, glyph, size = 32, className, render, ...props }: BrandMarkProps) {
  return useRender({
    defaultTagName: "span",
    render,
    props: mergeProps<"span">(
      {
        // A bare tile is an image with a name; as a link the link carries the name.
        role: render ? undefined : "img",
        "aria-label": name,
        className: cn(
          "inline-flex shrink-0 items-center justify-center bg-primary font-semibold text-primary-foreground select-none",
          SIZES[size],
          render
            ? `transition-colors duration-(--dur-fast) hover:bg-primary-hover ${FOCUS_RING}`
            : "",
          className,
        ),
        children: <span aria-hidden="true">{glyph ?? name.charAt(0).toUpperCase()}</span>,
      },
      props,
    ),
    state: { slot: "brand-mark" },
  });
}

/** A company's square tile (design spec §3.1 switcher): its initial on `--secondary`. */
function CompanyTile({
  name,
  size = 24,
  className,
  children,
  ...props
}: React.ComponentProps<"span"> & { name: string; size?: 20 | 24 }) {
  return (
    <span
      data-slot="company-tile"
      aria-hidden="true"
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-sm border border-border bg-secondary font-semibold text-secondary-foreground [&_svg]:size-3.5",
        size === 24 ? "size-6 text-xs" : "size-5 text-2xs",
        className,
      )}
      {...props}
    >
      {children ?? name.charAt(0).toUpperCase()}
    </span>
  );
}

export { BrandMark, CompanyTile };
